import { computed } from '@angular/core';
import {
    signalStore,
    withState,
    withComputed,
    withMethods,
    patchState,
} from '@ngrx/signals';
import { FilterObject } from '@app/shared/models/components/filter-object';
import {
    ViewType,
    ViewSearchConfig,
} from '@app/shared/models/view-config.interface';
import { PaginationInfo } from 'api-v3/models';
import {
    TableRowData,
} from '@app/shared/models/table-data-types';

export interface FilterKeys {
    keyName: string;
    label: string;
}

export interface PaginationState {
    offset: number;
    limit: number;
    hasMore: boolean;
    totalCount: number;
}

export interface SortState {
    sortBy: // Common fields
        | 'dateCreated'
        | 'number'
        | 'orderNumber'
        | 'name'
        | 'status'
        | 'bundleNumber'
        | 'tourNumber'

        // Date fields
        | 'checkoutDate'
        | 'checkinDate'
        | 'dateAnnounced'
        | 'dateClosed'

        // Organization fields
        | 'consignorName'
        | 'consigneeName'
        | 'carrierName'
        | 'originalConsignor'
        | 'originalConsignorGln'

        // Delivery note specific
        | 'deliveryNoteBundleNumber'
        | 'deliveryNoteBundleStatus'

        // Carrier fields
        | 'carrier'
        | 'carrierNumberPlateOutgoing'
        | 'carrierNumberPlateIncoming'
        | 'consignorLoadCarrierExchange'

        // Tour fields
        | 'externalTourName'

        // Status fields
        | 'discrepancies'
        | 'palletTickets'

        // Properties
        | 'properties'
        | 'properties.referenceNumber'

        // Notification specific fields
        | 'eventType'
        | 'bundleNr'
        | 'organizationSiteKey'
        | null;
    sortDirection: 'asc' | 'desc';
}

export interface SearchState {
    searchText: string;
    selectedFilterKey: FilterKeys | null;
    appliedFilters: FilterObject;
    pagination: PaginationState;
    sort: SortState;
    loading: boolean;
    currentViewType: ViewType;
    refreshTrigger: number;
    dataChangeSignal: {
        type: 'add' | 'update' | 'delete' | 'refresh';
        data?: TableRowData;
    };
}

export const initialSearchState: SearchState = {
    searchText: '',
    selectedFilterKey: null,
    appliedFilters: { text: '', checkClosed: true },
    pagination: {
        offset: 0,
        limit: 10,
        hasMore: false,
        totalCount: 0,
    },
    sort: {
        sortBy: 'dateCreated',
        sortDirection: 'desc',
    },
    loading: false,
    currentViewType: 'deliverynotes',
    refreshTrigger: 0,
    dataChangeSignal: { type: 'refresh', data: undefined },
};

const SEARCH_DEBOUNCE_MS = 300;

export const SearchStore = signalStore(
    { providedIn: 'root' },
    withState<SearchState>(initialSearchState),
    withComputed(store => ({
        hasActiveFilters: computed(() => {
            return (
                store.searchText() !== '' ||
                store.selectedFilterKey() !== null ||
                store.appliedFilters().text !== '' ||
                !store.appliedFilters().checkClosed
            );
        }),
        isFirstPage: computed(() => store.pagination().offset === 0),
        getCurrentPageIndex: computed(() => {
            const pagination = store.pagination();
            return Math.floor(pagination.offset / pagination.limit);
        }),
    })),
    withMethods(store => {
        // Private mutable variables for imperative side-effect state
        let currentAbortController: AbortController | null = null;
        let searchTextDebounceTimer: number | null = null;

        const triggerDataRefresh = () => {
            patchState(store, state => ({
                refreshTrigger: state.refreshTrigger + 1,
            }));
        };

        return {
            cancelOngoingRequests(): AbortSignal {
                if (
                    currentAbortController &&
                    !currentAbortController.signal.aborted
                ) {
                    currentAbortController.abort('New request initiated');
                }
                currentAbortController = new AbortController();
                return currentAbortController.signal;
            },

            triggerDataRefresh,

            updateSearchText(searchText: string): void {
                patchState(store, state => ({
                    searchText,
                    pagination: { ...state.pagination, offset: 0 },
                }));

                if (searchTextDebounceTimer !== null) {
                    clearTimeout(searchTextDebounceTimer);
                }

                searchTextDebounceTimer = window.setTimeout(() => {
                    triggerDataRefresh();
                    searchTextDebounceTimer = null;
                }, SEARCH_DEBOUNCE_MS);
            },

            updateSelectedFilterKey(filterKey: FilterKeys | null): void {
                patchState(store, state => ({
                    selectedFilterKey: filterKey,
                    pagination: { ...state.pagination, offset: 0 },
                }));
            },


            updateAppliedFilters(filters: FilterObject): void {
                patchState(store, { appliedFilters: filters });
            },

            updatePagination(pagination: Partial<PaginationState>): void {
                patchState(store, state => ({
                    pagination: { ...state.pagination, ...pagination },
                }));
            },

            updatePaginationFromApiResponse(
                paginationInfo: PaginationInfo
            ): void {
                patchState(store, state => ({
                    pagination: {
                        ...state.pagination,
                        offset: paginationInfo.offset,
                        limit: paginationInfo.limit,
                        hasMore: paginationInfo.hasMore,
                        totalCount: paginationInfo.totalCount,
                    },
                }));
                // NOTE: This deliberately does NOT call triggerDataRefresh() to prevent infinite loops
            },

            resetPaginationToEmpty(): void {
                patchState(store, state => ({
                    pagination: {
                        offset: 0,
                        limit: state.pagination.limit,
                        hasMore: false,
                        totalCount: 0,
                    },
                }));
            },

            updateSort(
                sortBy: SearchState['sort']['sortBy'],
                sortDirection?: 'asc' | 'desc'
            ): void {
                const currentSort = store.sort();
                let newSort: SortState;

                if (currentSort.sortBy === sortBy) {
                    if (currentSort.sortDirection === 'asc') {
                        newSort = { sortBy, sortDirection: 'desc' };
                    } else if (currentSort.sortDirection === 'desc') {
                        if (sortBy === 'dateCreated') {
                            newSort = {
                                sortBy: 'dateCreated',
                                sortDirection: 'asc',
                            };
                        } else {
                            newSort = {
                                sortBy: 'dateCreated',
                                sortDirection: 'desc',
                            };
                        }
                    } else {
                        newSort = {
                            sortBy,
                            sortDirection: sortDirection || 'asc',
                        };
                    }
                } else {
                    newSort = {
                        sortBy,
                        sortDirection: sortDirection || 'asc',
                    };
                }

                patchState(store, state => ({
                    sort: newSort,
                    pagination: { ...state.pagination, offset: 0 },
                }));
            },

            setLoading(loading: boolean): void {
                patchState(store, { loading });
            },

            updateCurrentViewType(
                viewType: ViewType,
                viewSearchConfig?: ViewSearchConfig
            ): void {
                let sort: SortState;

                if (viewSearchConfig) {
                    sort = {
                        sortBy: viewSearchConfig.defaultSortField as SortState['sortBy'],
                        sortDirection: viewSearchConfig.defaultSortDirection,
                    };
                } else {
                    // Fallback defaults
                    if (
                        viewType === 'deliverynotes' ||
                        viewType === 'folders' ||
                        viewType === 'tours'
                    ) {
                        sort = {
                            sortBy: 'dateCreated',
                            sortDirection: 'desc',
                        };
                    } else {
                        sort = {
                            sortBy: 'dateCreated',
                            sortDirection: 'desc',
                        };
                    }
                }

                patchState(store, state => ({
                    currentViewType: viewType,
                    pagination: { ...state.pagination, offset: 0 },
                    searchText: '',
                    selectedFilterKey: null,
                    sort,
                }));
            },

            signalDataAdded(data: TableRowData): void {
                patchState(store, { dataChangeSignal: { type: 'add', data } });
                triggerDataRefresh();
            },

            signalDataUpdated(data: TableRowData): void {
                patchState(store, {
                    dataChangeSignal: { type: 'update', data },
                });
                triggerDataRefresh();
            },

            signalDataDeleted(data: TableRowData): void {
                patchState(store, {
                    dataChangeSignal: { type: 'delete', data },
                });
                triggerDataRefresh();
            },

            goToPage(offset: number): void {
                patchState(store, state => ({
                    pagination: { ...state.pagination, offset },
                }));
            },

            goToNextPage(): void {
                patchState(store, state => ({
                    pagination: {
                        ...state.pagination,
                        offset:
                            state.pagination.offset + state.pagination.limit,
                    },
                }));
            },

            goToPreviousPage(): void {
                patchState(store, state => ({
                    pagination: {
                        ...state.pagination,
                        offset: Math.max(
                            0,
                            state.pagination.offset - state.pagination.limit
                        ),
                    },
                }));
            },

            resetSearch(): void {
                patchState(store, {
                    searchText: initialSearchState.searchText,
                    selectedFilterKey: initialSearchState.selectedFilterKey,
                    appliedFilters: initialSearchState.appliedFilters,
                    pagination: initialSearchState.pagination,
                    sort: initialSearchState.sort,
                    loading: initialSearchState.loading,
                    currentViewType: initialSearchState.currentViewType,
                });
            },

            resetPagination(): void {
                patchState(store, state => ({
                    pagination: {
                        ...initialSearchState.pagination,
                        limit: state.pagination.limit,
                    },
                }));
            },
        };
    })
);

